import { useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useApiQuery, useApiMutation } from './useApi'
import { personApi, PersonCreateRequest, PersonStatus, PersonUpdateRequest } from '../services/api/persons'

// Query keys
export const personKeys = {
  all: ['persons'] as const,
  lists: () => [...personKeys.all, 'list'] as const,
  list: (filters: any) => [...personKeys.lists(), filters] as const,
  details: () => [...personKeys.all, 'detail'] as const,
  detail: (id: number) => [...personKeys.details(), id] as const,
}

// Get persons list with pagination and filters
export function usePersons(params?: {
  page?: number
  size?: number
  search?: string
  status?: PersonStatus
  sort_by?: string
  sort_order?: string
}) {
  return useApiQuery({
    queryKey: personKeys.list(params),
    queryFn: () => personApi.getPersons(params),
    // React Query v5 renamed keepPreviousData -> placeholderData: keepPreviousData
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        return false
      }
      return failureCount < 1
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

// Get single person
export function usePerson(id: number) {
  return useApiQuery({
    queryKey: personKeys.detail(id),
    queryFn: () => personApi.getPerson(id),
    enabled: !!id,
  })
}

// Create person
export function useCreatePerson() {
  const queryClient = useQueryClient()

  return useApiMutation({
    mutationFn: (data: PersonCreateRequest) => personApi.createPerson(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.lists() })
    },
    successMessage: 'Person created successfully',
  })
}

// Update person
export function useUpdatePerson() {
  const queryClient = useQueryClient()

  return useApiMutation({
    mutationFn: ({ id, data }: { id: number; data: PersonUpdateRequest }) =>
      personApi.updatePerson(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: personKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: personKeys.lists() })
    },
    successMessage: 'Person updated successfully',
  })
}

// Delete person
export function useDeletePerson() {
  const queryClient = useQueryClient()

  return useApiMutation({
    mutationFn: (id: number) => personApi.deletePerson(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.lists() })
    },
    successMessage: 'Person deleted successfully',
  })
}
